import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';
import { PolicyPrice } from '../../policies/models/Policy';
import { AdminPortalAPIHttpClient } from '../../../infrastructure/http/v2/AdminPortalAPIHttpClient';
import { isStringEmptyOrNull } from '../../../utilities/StringUtility';

@Injectable({
  providedIn: 'root'
})
export class PriceRatesService {

  constructor(
    private adminPortalHttpClient: AdminPortalAPIHttpClient,
    private snackbar: MatSnackBar
  ) { }

  async getPriceRates(): Promise<PolicyPrice[]> {
    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.get(`/policy-prices/query`));
      return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(`Error while retrieving price rates: ${JSON.stringify(e)}`);
    }
  }

  async getPriceRateDetail(priceRateId: string): Promise<PolicyPrice> {
    const response: any = await firstValueFrom(this.adminPortalHttpClient.get(`/policy-prices/${priceRateId}`));
    return Promise.resolve(response?.data);
  }

  async createPriceRate(priceRate: any): Promise<PolicyPrice> {
    try {
      const payload: PolicyPrice = {
        id: priceRate.id,
        type_id: priceRate.type_id,
        name: priceRate.name,
        description: priceRate.description,
        currency_id: priceRate.currency_id,
        currency: priceRate.currency,
        pricing_model_id: priceRate.pricing_model_id,
        deductible: priceRate.deductible,
        price: priceRate.price,
        extra_night_price: priceRate.extra_night_price ? priceRate.extra_night_price : 0,
        max_nights: null,
        status_id: 1,
        is_active: true,
        night_ranges: priceRate.night_ranges,
      }

      const response: any = await firstValueFrom(this.adminPortalHttpClient.post(`/policy-prices`, payload));
      if (!response) {
        return Promise.reject(`Create price rate response null`);
      } else {
        if (response.err != null && !isStringEmptyOrNull(response.err.msg)) {
          this.displaySnackbar(response.err.msg);
        } else {
          this.displaySnackbar(response.err);
        }
      }

      return response.data;
    } catch (e) {
      return Promise.reject(`Error while creating price rate: ${e}`);
    }
  }

  async updatePriceRate(priceRateId: any, priceRate: any): Promise<any> {
    try {
      const response: any = await firstValueFrom(this.adminPortalHttpClient.put(`/policy-prices/${priceRateId}`, priceRate));
      if (!response) {
        return Promise.reject(`Update price rate response null ${priceRateId}`);
      } else {
        if (response.err != null && !isStringEmptyOrNull(response.err.msg)) {
          this.displaySnackbar(response.err.msg);
        } else {
          this.displaySnackbar(response.err);
        }
      }

      return Promise.resolve(response.data);
    } catch (e) {
      return Promise.reject(`Error while updating price rate ${priceRateId}: ${e}`);
    }
  }


  displaySnackbar(errorMessage: string) {
    if (isStringEmptyOrNull(errorMessage)) {
      this.displaySuccessSnackbar();
    } else {
      this.displayErrorSnackbar(errorMessage);
    }
  }
    
  displaySuccessSnackbar() {
    this.snackbar.open('Save Successful', 'OK', { duration: 4000 });
  }

  displayErrorSnackbar(errorMessage: string) {
    this.snackbar.open(`Error Saving Policy: ${errorMessage}`, 'OK', { duration: 4000 });
  }

  getPriceModelById = (id: number): string => {
    switch(id) {
      case 1:
        return 'Tiered nights';
      case 2:
        return 'Per night';
      case 3:
        return 'Per reservation';
      default:
        return '';
    }
  }
}